import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { practiceAPI } from '../../api/client'
import { useExamFramework } from '../../hooks/useExamFramework'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

/** FR-009 Reading Training Module — parts rendered dynamically per exam (FR-029) */
export default function ReadingPage() {
  const { parts, partLabel, displayName, examType, isLoading } = useExamFramework()
  const readingParts = parts('reading')
  const [activePart, setActivePart] = useState<string>('')

  useEffect(() => {
    if (!activePart && readingParts.length > 0) setActivePart(readingParts[0].partCode)
  }, [readingParts, activePart])

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>menu_book</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Reading</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Luyện đọc <span style={{ color: '#004be2', fontStyle: 'italic' }}>{displayName}</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          {readingParts.length} phần đọc theo chuẩn thi {displayName}.
        </p>
      </div>

      {/* Part tabs (data-driven) */}
      {isLoading || readingParts.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>
      ) : (
        <>
          <div className="scroll-tabs" style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem' }}>
            {readingParts.map(part => {
              const sel = activePart === part.partCode
              return (
                <button
                  key={part.partCode}
                  onClick={() => setActivePart(part.partCode)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '1rem 1.5rem', borderRadius: '1rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                    background: sel ? 'linear-gradient(135deg, #004be2, #809bff)' : 'white',
                    boxShadow: sel ? '0 8px 20px rgba(0,75,226,0.25)' : '0 4px 12px rgba(0,0,0,0.04)',
                    transform: sel ? 'scale(1.02)' : 'none',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ color: sel ? 'white' : '#004be2', fontSize: '1.25rem', fontVariationSettings: sel ? "'FILL' 1" : "'FILL' 0" }}>article</span>
                  <div style={{ textAlign: 'left' }}>
                    <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, color: sel ? 'white' : '#272e42' }}>{partLabel(part)}</H>
                    <span style={{ fontSize: '0.75rem', color: sel ? 'rgba(255,255,255,0.75)' : '#535b71' }}>{part.questionCount ? `${part.questionCount} câu` : ''}</span>
                  </div>
                </button>
              )
            })}
          </div>

          {activePart && <ReadingExercise skill="reading" part={activePart} examType={examType} key={activePart} />}
        </>
      )}
    </AppLayout>
  )
}

// ─── Reading Exercise ─────────────────────────────────────────────────────────

function ReadingExercise({ skill, part, examType }: { skill: string; part: string; examType: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['practice', skill, part, examType],
    queryFn: () => practiceAPI.getQuestions(skill, part, examType),
  })

  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [results, setResults] = useState<Record<string, { isCorrect: boolean | null; correctAnswer: string; explanation: string }>>({})
  const [submitted, setSubmitted] = useState(false)
  const [elapsedSec, setElapsedSec] = useState(0)
  const [startTime] = useState(() => Date.now())

  const submitAnswer = useMutation({
    mutationFn: ({ questionId, answer, time }: { questionId: string; answer: string; time: number }) =>
      practiceAPI.submitAnswer(questionId, answer, time),
  })

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <div className="spinner" />
    </div>
  )

  if (error) return (
    <div style={{ padding: '2rem', background: 'rgba(180,19,64,0.08)', borderRadius: '1rem', color: '#b41340', fontWeight: 600 }}>
      Không thể tải bài đọc. Vui lòng thử lại.
    </div>
  )

  const { content, questions, aiAnswers, source } = data ?? {}

  if (!questions || questions.length === 0) return (
    <div style={{ textAlign: 'center', padding: '4rem', background: 'white', borderRadius: '1.5rem' }}>
      <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>pending</span>
      <H style={{ display: 'block', fontWeight: 700, marginBottom: '0.5rem', color: '#535b71' }}>Nội dung bài đọc đang được cập nhật</H>
      <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Admin có thể thêm nội dung qua CMS.</p>
    </div>
  )

  async function handleSubmitAll() {
    const elapsed = Math.round((Date.now() - startTime) / 1000)
    setElapsedSec(elapsed)
    const newResults: typeof results = {}
    for (const q of questions!) {
      const answer = answers[q.id] ?? ''
      if (!answer) continue
      const timePerQ = Math.round(elapsed / questions!.length)
      if (source === 'ai_generated' && aiAnswers) {
        const idx = questions!.indexOf(q)
        const ai = aiAnswers[idx]
        newResults[q.id] = {
          isCorrect: answer.trim().toLowerCase() === ai.correctAnswer.trim().toLowerCase(),
          correctAnswer: ai.correctAnswer,
          explanation: ai.explanation,
        }
      } else {
        const res = await submitAnswer.mutateAsync({ questionId: q.id, answer, time: timePerQ })
        newResults[q.id] = res
      }
    }
    setResults(newResults)
    setSubmitted(true)
  }

  const allAnswered = questions.every((q: any) => answers[q.id])
  const score = Object.values(results).filter(r => r.isCorrect).length
  const mins = Math.floor(elapsedSec / 60)
  const secs = elapsedSec % 60

  return (
    <div className={content?.text ? 'drill-2col' : ''} style={{ display: 'grid', gridTemplateColumns: content?.text ? '1fr 1fr' : '1fr', gap: '1.5rem', alignItems: 'start' }}>
      {/* Passage */}
      {content?.text && (
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)', position: 'sticky', top: '5rem', maxHeight: 'calc(100vh - 8rem)', overflowY: 'auto' }}>
          <div style={{ marginBottom: '1.25rem' }}>
            {content.title && <H style={{ display: 'block', fontWeight: 800, fontSize: '1.125rem', marginBottom: '0.375rem' }}>{content.title}</H>}
            {content.source && (
              <span style={{ padding: '0.25rem 0.75rem', background: '#eef0ff', borderRadius: '9999px', fontSize: '0.6875rem', fontWeight: 700, color: '#004be2' }}>{content.source}</span>
            )}
          </div>
          <p style={{ fontSize: '0.9375rem', lineHeight: 1.85, color: '#272e42' }}>{content.text}</p>
        </div>
      )}

      {/* Questions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {questions.map((q: any, idx: number) => (
          // Render by question shape, not by a hardcoded VSTEP part: MCQ when it
          // has options, gap-fill text input otherwise. Works for any exam (FR-029).
          (q.options && q.options.length > 0)
            ? <QuestionCard key={q.id} index={idx + 1} question={q} selectedAnswer={answers[q.id]} result={submitted ? results[q.id] : undefined} onAnswer={ans => setAnswers(p => ({ ...p, [q.id]: ans }))} disabled={submitted} />
            : <GapFillCard key={q.id} index={idx + 1} question={q} value={answers[q.id] ?? ''} result={submitted ? results[q.id] : undefined} onChange={v => setAnswers(p => ({ ...p, [q.id]: v }))} disabled={submitted} />
        ))}

        {/* Submit / Result */}
        {!submitted ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', padding: '1.5rem', background: 'white', borderRadius: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
            <span style={{ fontSize: '0.875rem', color: '#535b71', fontWeight: 600 }}>
              {Object.keys(answers).length}/{questions.length} câu đã trả lời
            </span>
            <button
              onClick={handleSubmitAll}
              disabled={!allAnswered || submitAnswer.isPending}
              style={{
                marginLeft: 'auto', padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none',
                cursor: allAnswered ? 'pointer' : 'not-allowed',
                background: allAnswered ? 'linear-gradient(135deg, #004be2, #809bff)' : '#d1dcff',
                color: allAnswered ? 'white' : '#a5adc6',
                fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem',
              }}
            >
              {submitAnswer.isPending ? 'Đang chấm...' : 'Nộp bài'}
            </button>
          </div>
        ) : (
          <div style={{ padding: '1.5rem', background: 'white', borderRadius: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 900, color: '#004be2' }}>{score}/{questions.length}</H>
              <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>câu đúng{elapsedSec > 0 ? ` · ${mins}p${secs}s` : ''}</span>
            </div>
            <button
              onClick={() => { refetch(); setAnswers({}); setResults({}); setSubmitted(false) }}
              style={{ padding: '0.875rem 1.75rem', borderRadius: '9999px', border: 'none', background: '#060e20', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}
            >
              Bài tiếp theo →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Question components ──────────────────────────────────────────────────────

function GapFillCard({ index, question, value, result, onChange, disabled }: {
  index: number; question: any; value: string
  result?: { isCorrect: boolean | null; correctAnswer: string; explanation: string }
  onChange: (v: string) => void; disabled: boolean
}) {
  const borderColor = result ? (result.isCorrect ? '#004be2' : '#b41340') : '#d1dcff'
  const bg = result ? (result.isCorrect ? 'rgba(0,75,226,0.04)' : 'rgba(180,19,64,0.04)') : 'white'

  return (
    <div style={{ background: bg, borderRadius: '1rem', padding: '1.25rem', border: `1.5px solid ${borderColor}`, transition: 'all 0.2s' }}>
      <p style={{ fontSize: '0.9375rem', color: '#272e42', marginBottom: '0.875rem', lineHeight: 1.6 }}>
        <strong style={{ color: '#004be2' }}>{index}.</strong> {question.prompt}
      </p>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        placeholder="Điền từ..."
        style={{
          width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem',
          background: '#eef0ff', border: 'none', outline: 'none',
          fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', color: '#272e42',
        }}
      />
      {result && (
        <p style={{ marginTop: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: result.isCorrect ? '#004be2' : '#b41340' }}>
          {result.isCorrect ? '✓ Đúng!' : `✗ Đáp án: ${result.correctAnswer}`}
          {result.explanation && <span style={{ fontWeight: 400, color: '#535b71', display: 'block' }}>{result.explanation}</span>}
        </p>
      )}
    </div>
  )
}

function QuestionCard({ index, question, selectedAnswer, result, onAnswer, disabled }: {
  index: number; question: any; selectedAnswer?: string
  result?: { isCorrect: boolean | null; correctAnswer: string; explanation: string }
  onAnswer: (ans: string) => void; disabled: boolean
}) {
  return (
    <div style={{ background: 'white', borderRadius: '1.25rem', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
      <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#272e42', marginBottom: '1rem', lineHeight: 1.6 }}>
        <strong style={{ color: '#004be2' }}>Câu {index}:</strong> {question.prompt}
      </p>
      {question.options ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {question.options.map((opt: string, i: number) => {
            const label = String.fromCharCode(65 + i)
            const isSel = selectedAnswer === label
            const isCorrectOpt = result?.correctAnswer === label
            let bg = 'transparent', border = '1.5px solid #d1dcff', color = '#272e42'
            if (result) {
              if (isCorrectOpt) { bg = 'rgba(0,75,226,0.08)'; border = '1.5px solid #004be2'; color = '#004be2' }
              else if (isSel && !result.isCorrect) { bg = 'rgba(180,19,64,0.06)'; border = '1.5px solid #b41340'; color = '#b41340' }
            } else if (isSel) { bg = 'rgba(0,75,226,0.06)'; border = '1.5px solid rgba(0,75,226,0.4)' }
            return (
              <button
                key={i}
                onClick={() => !disabled && onAnswer(label)}
                disabled={disabled}
                style={{
                  width: '100%', textAlign: 'left', padding: '0.875rem 1rem', borderRadius: '0.875rem',
                  background: bg, border, color, cursor: disabled ? 'default' : 'pointer',
                  fontSize: '0.9rem', fontWeight: 500, transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: '0.875rem',
                }}
              >
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.875rem', opacity: 0.6 }}>{label}.</span>
                {opt}
              </button>
            )
          })}
        </div>
      ) : (
        <input
          type="text"
          value={selectedAnswer ?? ''}
          onChange={e => onAnswer(e.target.value)}
          disabled={disabled}
          placeholder="Nhập câu trả lời..."
          style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem' }}
        />
      )}
      {result && (
        <p style={{ marginTop: '0.75rem', fontSize: '0.8125rem', fontWeight: 600, color: result.isCorrect ? '#004be2' : '#b41340', lineHeight: 1.5 }}>
          {result.isCorrect ? '✓ Đúng!' : `✗ Đáp án: ${result.correctAnswer}`}
          {result.explanation && <span style={{ display: 'block', fontWeight: 400, color: '#535b71' }}>{result.explanation}</span>}
        </p>
      )}
    </div>
  )
}
